"use client";
import Button from "@/components/common/buttons/Button";
import images from "@/config/images";
import yup from "@/config/yup.custom";
import { freeEmailDomains } from "@/constants/businessEmail";
import { setErrorMess } from "@/lib/features/statusSlice";
import { montserrat } from "@/lib/font";
import { Lang, OptionItem } from "@/models/general";
import { Report } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import { yupResolver } from "@hookform/resolvers/yup";
import { setDownload } from "@lib/features/reportSlice";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Inputs } from "../../../../../../../components/Inputs";

interface PopupDownloadProps {
  handleClose: () => void;
  open: boolean;
  reportId?: number;
  dataDetail?: Report;
  isLogin?: boolean;
}

export interface PopupDownloadFormData {
  lastname: string;
  firstname: string;
  company: string;
  email: string;
  language: string;
  captcha?: string;
}

const PopupDownload = (props: PopupDownloadProps) => {
  const t = useTranslations("messages");
  const { open, handleClose, reportId, dataDetail, isLogin } = props;
  const [isShowErrorBusinessEmail, setisShowErrorBusinessEmail] =
    useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const dispatch = useDispatch();
  const langOptionsTranslate: OptionItem<string>[] = useMemo(
    () => [
      {
        id: Lang.EN,
        name: t("common_english"),
        img: images.imgLangEn,
      },
      {
        id: Lang.VI,
        name: t("common_vietnamese"),
        img: images.imgLangVi,
      },
      {
        id: Lang.ID,
        name: t("common_indonesia"),
        img: images.imgLangId,
      },
    ],
    [t]
  );

  const handleCloseDownload = () => {
    clearErrors();
    handleClose();
  };

  const queryClient = useQueryClient();
  const onChangeCaptCha = (value: any) => {
    setValue("captcha", value);
  };
  const schema = useMemo(() => {
    return yup.object().shape(
      isLogin
        ? {
            language: yup.string().nullable().required("Language is required"),
          }
        : {
            firstname: yup
              .string()
              .trim()
              .required(t("field_firstname_required"))
              .max(255, t("field_firstname_max"))
              .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, t("field_firstname_format")),
            lastname: yup
              .string()
              .trim()
              .required(t("field_lastname_required"))
              .max(255, t("field_lastname_max"))
              .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, t("field_lastname_format")),
            company: yup
              .string()
              .trim()
              .required(() => t("field_company_vali_required"))
              .test(
                "is-valid-company",
                () => t("field_company_vali_company"),
                (value) => !value || /^[A-Za-zÀ-Ỹà-ỹ\s]+$/.test(value)
              ),
            email: yup
              .string()
              .trim()
              .email(() => t("field_email_vali_email"))
              .required(() => t("field_email_vali_required")),
            // .test(
            //   "invalid_email",
            //   () => t("email_business"),
            //   (value) =>
            //     !value ||
            //     !freeEmailDomains.some((domain) => value.endsWith(domain))
            // ),
            language: yup.string().nullable().notRequired(),
            captcha: yup.string().when([], {
              is: () => showCaptcha,
              then: (schema) => schema.required("Captcha is required"),
              otherwise: (schema) => schema.optional(),
            }),
          }
    );
  }, [isLogin]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<PopupDownloadFormData | any>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const mutationPost = useMutation({
    mutationFn: async (pageParam: FormData) => {
      const result = await ReportServices.postReportsDownload(pageParam);
      return result;
    },
    onSuccess: (response: any) => {
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      handleCloseDownload();
      dispatch(setDownload(true));
    },
    onError: (error: any) => {
      reset({
        lastname: getValues("lastname"),
        firstname: getValues("firstname"),
        company: getValues("company"),
        email: getValues("email"),
      });
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      dispatch(setErrorMess(error?.response?.data?.detail));
    },
  });

  useEffect(() => {
    if (!isLogin) {
      const localStorageData = localStorage.getItem("infoDownload");
      if (localStorageData) {
        const { email, lastName, firstName, company } =
          JSON.parse(localStorageData);
        reset({
          lastname: lastName,
          firstname: firstName,
          company: company,
          email: email,
        });
      }
    }
  }, [isLogin]);
  const handleReportAttachmentLang = useMemo(() => {
    const languages =
      dataDetail?.reportAttachments?.map((attachment) => attachment.language) ||
      [];
    return languages;
  }, [dataDetail]);
  const handleSetReportAttachment = useMemo(() => {
    const languages = new Set(
      dataDetail?.reportAttachments
        ?.filter((attachment) => attachment?.language)
        ?.map((attachment) => attachment.language) || []
    );
    if (isLogin) {
      return languages.add("en");
    }
    return languages;
  }, [dataDetail, isLogin]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const availableLanguages = useMemo(() => {
    return langOptionsTranslate.filter((item) =>
      handleSetReportAttachment.has(item.id)
    );
  }, [langOptionsTranslate, handleSetReportAttachment]);
  const onSubmit = async (data: PopupDownloadFormData) => {
    if (!isLogin && !data.captcha) {
      setShowCaptcha(true);
      return;
    }
    if (!isLogin && data.email) {
      const splitEmail = data.email.split("@")[1];
      const isCheckBusinessEmail = freeEmailDomains.includes(
        splitEmail.toLowerCase()
      );
      if (isCheckBusinessEmail) {
        setisShowErrorBusinessEmail(true);
        return;
      }
    }
    setisShowErrorBusinessEmail(false);
    clearErrors();
    const formData = new FormData();
    formData.append("reportId", reportId ? reportId.toString() : "");
    if (!isLogin) {
      formData.append("email", data.email);
      formData.append("lastName", data.lastname);
      formData.append("firstName", data.firstname);
      formData.append("company", data.company);
      formData.append("captcha", data.captcha || "");
      localStorage.setItem(
        "infoDownload",
        JSON.stringify({
          email: data.email,
          lastName: data.lastname,
          firstName: data.firstname,
          company: data.company,
        })
      );
    }
    if (handleReportAttachmentLang.includes(data?.language)) {
      formData.append("language", data.language.toString());
    } else {
      formData.append("language", "");
    }
    await mutationPost.mutateAsync(formData);
  };
  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={handleCloseDownload}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit(onSubmit),
        },
      }}
    >
      <DialogTitle
        component="div"
        className="flex justify-between items-center !pb-0"
      >
        <Box className="flex items-center">
          <p
            className="text-ciBlue_900 font-semibold text-2xl/6 m-0"
            translation-key="popup_download_title"
          >
            {t("popup_download_title")}
          </p>
        </Box>
        <Box>
          <IconButton
            className="!bg-eerie_black_5 size-8 !p-1"
            onClick={handleCloseDownload}
          >
            <CloseIcon className="text-[rgba(28, 28, 28, 0.4)]" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={`gap-8 grid px-8 pt-6 pb-8 ${montserrat}`}>
        <Box className="gap-6 grid">
          <Box className="gap-4 grid">
            <p
              className="font-normal text-base/6 text-gray_90 m-0"
              translation-key="popup_download_instructions"
            >
              {t("popup_download_instructions")}
            </p>
            <p
              className="font-normal text-base/6 text-gray_90 m-0"
              translation-key="popup_download_marketing_agreement"
            >
              {t("popup_download_marketing_agreement")}
            </p>
          </Box>
          <Box className="gap-8 grid">
            <Box className="gap-4 grid">
              {handleSetReportAttachment.size > 0 && (
                <Box className="flex flex-col gap-1">
                  <InputLabel
                    id="label-select-language"
                    className={"!font-semibold !text-base/6 !text-gray_80"}
                    translation-key="common_language"
                  >
                    {t("common_language")}
                  </InputLabel>
                  <Select
                    fullWidth
                    {...register("language")}
                    labelId="label-select-language"
                    id="select-language"
                    value={selectedLanguage}
                    onChange={(event) => {
                      setSelectedLanguage(event.target.value);
                      setValue("language", event.target.value);
                    }}
                    sx={{
                      "& .MuiSelect-select": {
                        paddingY: "8.5px !important",
                      },
                    }}
                  >
                    {availableLanguages.map((item, index) => (
                      <MenuItem key={index} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
              {!isLogin && (
                <>
                  <Box>
                    <Inputs
                      translation-key="input_firstname_title"
                      translation-key-placeholder="input_firstname_placeholder"
                      title={t("input_firstname_title")} // Key for title
                      name="firstname"
                      className="h-10"
                      inputRef={register("firstname")}
                      errorMessage={errors.firstname?.message?.toString()}
                      placeholder={t("input_firstname_placeholder")} // Key for placeholder
                    />
                  </Box>
                  <Box>
                    <Inputs
                      translation-key="input_lastname_title"
                      translation-key-placeholder="input_lastname_placeholder"
                      title={t("input_lastname_title")} // Key for title
                      name="lastname"
                      className="h-10"
                      inputRef={register("lastname")}
                      errorMessage={errors.lastname?.message?.toString()}
                      placeholder={t("input_lastname_placeholder")} // Key for placeholder
                    />
                  </Box>
                  <Box>
                    <Inputs
                      translation-key="input_company_title"
                      translation-key-placeholder="input_company_placeholder"
                      title={t("input_company_title")} // Key for title
                      name="company"
                      className="h-10"
                      inputRef={register("company")}
                      errorMessage={errors.company?.message?.toString()}
                      placeholder={t("input_company_placeholder")} // Key for placeholder
                    />
                  </Box>
                  <Box>
                    <Inputs
                      translation-key="input_email_title"
                      translation-key-placeholder="input_email_placeholder"
                      type="email"
                      title={t("input_email_title")} // Key for title
                      name="email"
                      className="h-10"
                      inputRef={register("email")}
                      errorMessage={errors.email?.message?.toString()}
                      placeholder={t("input_email_placeholder")} // Key for placeholder
                    />
                  </Box>
                  {isShowErrorBusinessEmail && (
                    <Box
                      translation-key="error_email_download"
                      className="text-red-500 text-sm text-start"
                    >
                      {t.rich("error_email_download", {
                        span: (chunk) => (
                          <span className="font-semibold">{chunk}</span>
                        ),
                        email: "ask@cimigo.com",
                      })}
                    </Box>
                  )}
                  <Box className="flex flex-col gap-[5px] justify-center items-center">
                    <ReCAPTCHA
                      lang="en"
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                      ref={recaptchaRef}
                      onChange={onChangeCaptCha}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="!pb-8 !px-6 !pt-0">
        <Button
          isLoading={mutationPost.isPending}
          disabled={mutationPost.isPending}
          variant="text"
          type="submit"
          className="!py-2 !px-4 gap-1 text-center w-full !bg-ciBlue_900 hover:!bg-ciBlue_light_2 disabled:cursor-not-allowed disabled:bg-gray_80 disabled:text-gray_40 download-get-report-button"
        >
          <span
            className="text-white font-medium text-lg/8 normal-case"
            translation-key="popup_download_get_report_button"
          >
            {t("popup_download_get_report_button")}
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupDownload;
